/**
 * Mis comprobantes (`/comprobantes`).
 *
 * Portado de `views/portal_cliente/facturas.php` + `api_facturas`: los tres
 * orígenes del portal antiguo (facturas y boletas de **playa** —que salen de la
 * venta con el documento del cliente— y las facturas **administrativas** a
 * crédito), con los mismos filtros y el detalle de una factura administrativa.
 *
 * Diferencia anotada: el portal antiguo exportaba a Excel con PhpSpreadsheet;
 * aquí el botón genera **CSV** (el mismo exportador del panel), que abre igual en
 * Excel.
 */

import { Download, Filter, Receipt, RefreshCw, RotateCcw, Search, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Buscador } from '@/components/ui/buscador';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Paginador } from '@/components/ui/paginador';
import { SectionCard } from '@/components/ui/card';
import { EstadoVacio } from '@/components/ui/estado-vacio';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { TituloPagina } from '@/components/ui/titulo-pagina';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { usePaginacion } from '@/hooks/use-paginacion';
import { ApiError } from '@/lib/http/api-error';
import { coincideBusqueda } from '@/lib/buscar';
import { exportarCsv } from '@/lib/export';
import { cn, formatCurrency, formatDateValue } from '@/lib/utils';

import { usePortalFacturaDetalle, usePortalFacturas } from '../portal.hooks';
import type { FacturaPortal } from '../portal.types';

const TONO_PAGO: Record<string, 'success' | 'warning' | 'danger' | 'neutral'> = {
  Pagado: 'success',
  Vigente: 'neutral',
  'Por vencer': 'warning',
  Vencido: 'danger',
};

type VistaRapida = 'todos' | 'saldo' | 'vencidos' | 'pagados';

/** Pestañas rápidas que acompañan al buscador (se suman a los filtros del servidor). */
const VISTAS_RAPIDAS: { valor: VistaRapida; etiqueta: string }[] = [
  { valor: 'todos', etiqueta: 'Todos' },
  { valor: 'saldo', etiqueta: 'Con saldo' },
  { valor: 'vencidos', etiqueta: 'Vencidos' },
  { valor: 'pagados', etiqueta: 'Pagados' },
];

/** Los comprobantes de playa se cobran en el surtidor; los a crédito mandan su estado. */
function estaPagado(factura: FacturaPortal): boolean {
  return factura.origen !== 'administrativa' || factura.estado_pago === 'Pagado';
}

/** ¿La fila pertenece a la pestaña rápida elegida? */
function coincideVista(factura: FacturaPortal, vista: VistaRapida): boolean {
  if (vista === 'saldo') return (factura.saldo ?? 0) > 0;
  if (vista === 'vencidos') return factura.estado_pago === 'Vencido';
  if (vista === 'pagados') return estaPagado(factura);

  return true;
}

/** Campos por los que se puede encontrar una fila desde el buscador. */
function camposBuscables(factura: FacturaPortal): unknown[] {
  const campos: unknown[] = [
    factura.documento,
    factura.tipo,
    factura.origen === 'boleta' ? 'boleta' : 'factura',
    factura.estado,
    factura.estado_pago,
    factura.placa,
    formatDateValue(factura.fecha),
    formatCurrency(factura.total),
  ];

  // El saldo sólo si existe: en playa es `undefined` y «S/ 0.00» haría que
  // cualquier búsqueda con un cero devolviera la tabla entera.
  if (factura.saldo !== undefined) campos.push(formatCurrency(factura.saldo));

  return campos;
}

/** Etiqueta de estado: los comprobantes de playa sólo tienen el estado SUNAT. */
function EstadoComprobante({ factura }: { factura: FacturaPortal }) {
  if (factura.origen !== 'administrativa') {
    return <Badge variant="neutral">{factura.estado}</Badge>;
  }

  return (
    <Badge variant={TONO_PAGO[factura.estado_pago ?? ''] ?? 'neutral'}>
      {factura.estado_pago ?? factura.estado}
    </Badge>
  );
}

export function PortalFacturasPage() {
  useDocumentTitle('Portal · Comprobantes');
  const [parametros, setParametros] = useSearchParams();
  const [detalleId, setDetalleId] = useState<number | null>(null);
  const [busqueda, setBusqueda] = useState('');
  const [vista, setVista] = useState<VistaRapida>('todos');

  const filtros = useMemo(
    () => ({
      desde: parametros.get('desde') ?? '',
      hasta: parametros.get('hasta') ?? '',
      serie: parametros.get('serie') ?? '',
      numero: parametros.get('numero') ?? '',
      placa: parametros.get('placa') ?? '',
      origen: parametros.get('origen') ?? 'todos',
    }),
    [parametros],
  );

  const consulta = usePortalFacturas({
    desde: filtros.desde,
    hasta: filtros.hasta,
    serie: filtros.serie,
    numero: filtros.numero,
    placa: filtros.placa,
    origen: filtros.origen,
  });

  const cambiar = (campo: string, valor: string) => {
    const siguiente = new URLSearchParams(parametros);

    if (valor) siguiente.set(campo, valor);
    else siguiente.delete(campo);

    setParametros(siguiente, { replace: true });
  };

  const limpiar = () => {
    setBusqueda('');
    setVista('todos');
    setParametros(new URLSearchParams(), { replace: true });
  };

  const facturas = useMemo(() => consulta.data?.facturas ?? [], [consulta.data]);

  // Buscador y pestañas rápidas: se aplican en el navegador sobre lo que ya
  // devolvió el servidor (que filtró por fecha, serie, número, placa y origen).
  const filtradas = useMemo(
    () =>
      facturas.filter(
        (factura) =>
          coincideVista(factura, vista) && coincideBusqueda(camposBuscables(factura), busqueda),
      ),
    [facturas, busqueda, vista],
  );

  const conteos = useMemo(
    () => ({
      todos: facturas.length,
      saldo: facturas.filter((factura) => coincideVista(factura, 'saldo')).length,
      vencidos: facturas.filter((factura) => coincideVista(factura, 'vencidos')).length,
      pagados: facturas.filter((factura) => coincideVista(factura, 'pagados')).length,
    }),
    [facturas],
  );

  // `clave` reinicia a la primera página al buscar o cambiar de pestaña. Es un
  // texto (valor estable) para no reiniciar en cada render.
  const paginacion = usePaginacion(filtradas, {
    clave: [
      busqueda,
      vista,
      filtros.desde,
      filtros.hasta,
      filtros.serie,
      filtros.numero,
      filtros.placa,
      filtros.origen,
    ].join('|'),
  });

  const exportar = () => {
    const filas = filtradas.map((factura) => [
      factura.documento,
      factura.tipo ?? (factura.origen === 'boleta' ? 'Boleta' : 'Factura'),
      formatDateValue(factura.fecha),
      factura.vencimiento ? formatDateValue(factura.vencimiento) : '',
      factura.total.toFixed(2),
      (factura.saldo ?? 0).toFixed(2),
      factura.estado_pago ?? factura.estado,
    ]);

    exportarCsv(
      'comprobantes-portal',
      ['Documento', 'Tipo', 'Emitido', 'Vence', 'Total', 'Saldo', 'Estado'],
      filas,
    );
  };

  const totales = filtradas.reduce(
    (suma, factura) => ({
      total: suma.total + factura.total,
      saldo: suma.saldo + (factura.saldo ?? 0),
    }),
    { total: 0, saldo: 0 },
  );

  const etiquetaVista = VISTAS_RAPIDAS.find((opcion) => opcion.valor === vista)?.etiqueta ?? 'Todos';
  const hayFiltroLocal = busqueda.trim() !== '' || vista !== 'todos';

  const quitarFiltros = () => {
    setBusqueda('');
    setVista('todos');
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <TituloPagina
          icono={Receipt}
          titulo="Mis comprobantes"
          descripcion="Facturas y boletas de tus cargas, y las facturas a crédito de tu cuenta."
        />
        <Button
          variant="outline"
          size="sm"
          leftIcon={<Download className="size-3.5" aria-hidden="true" />}
          disabled={filtradas.length === 0}
          onClick={exportar}
        >
          Descargar CSV
        </Button>
      </div>

      <SectionCard
        title="Filtros"
        description="Por fecha, serie, número o placa."
        action={
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<RotateCcw className="size-3.5" aria-hidden="true" />}
            onClick={limpiar}
          >
            Limpiar
          </Button>
        }
      >
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          <Input
            label="Desde"
            type="date"
            value={filtros.desde}
            onChange={(evento) => cambiar('desde', evento.target.value)}
          />
          <Input
            label="Hasta"
            type="date"
            value={filtros.hasta}
            onChange={(evento) => cambiar('hasta', evento.target.value)}
          />
          <div className="flex flex-col gap-1.5">
            <label htmlFor="origen-filtro" className="text-sm font-medium text-ink-700">
              Origen
            </label>
            <Select
              id="origen-filtro"
              value={filtros.origen}
              onChange={(evento) => cambiar('origen', evento.target.value)}
            >
              <option value="todos">Todos</option>
              <option value="playa">Sólo playa (facturas y boletas)</option>
              <option value="administrativa">Sólo facturas a crédito</option>
            </Select>
          </div>
          <Input
            label="Serie"
            value={filtros.serie}
            placeholder="FA03"
            onChange={(evento) => cambiar('serie', evento.target.value)}
          />
          <Input
            label="Número"
            value={filtros.numero}
            placeholder="234"
            onChange={(evento) => cambiar('numero', evento.target.value)}
          />
          <Input
            label="Placa"
            value={filtros.placa}
            placeholder="ABC-123"
            onChange={(evento) => cambiar('placa', evento.target.value)}
          />
        </div>
        <p className="mt-2 flex items-center gap-1.5 text-xs text-ink-400">
          <Filter className="size-3.5" aria-hidden="true" />
          Los filtros se aplican al instante; el rango de fechas necesita los dos campos.
        </p>
      </SectionCard>

      {consulta.error ? (
        <Alert variant="error">
          {consulta.error instanceof ApiError
            ? consulta.error.message
            : 'No se pudieron cargar tus comprobantes.'}
        </Alert>
      ) : null}

      <SectionCard
        title={`Comprobantes (${consulta.data?.total ?? 0})`}
        description={
          consulta.data
            ? `${busqueda.trim() || vista !== 'todos' ? `${filtradas.length} de ${facturas.length} · ` : ''}${consulta.data.administrativas} a crédito y ${consulta.data.playa} de playa · total ${formatCurrency(totales.total)} · por pagar ${formatCurrency(totales.saldo)}`
            : 'Cargando…'
        }
        action={
          <Button
            variant="ghost"
            size="sm"
            leftIcon={
              <RefreshCw
                className={cn('size-3.5', consulta.isFetching && 'animate-spin')}
                aria-hidden="true"
              />
            }
            onClick={() => void consulta.refetch()}
            disabled={consulta.isFetching}
          >
            Actualizar
          </Button>
        }
      >
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <Buscador
              valor={busqueda}
              onChange={setBusqueda}
              ariaLabel="Buscar comprobantes"
              placeholder="Buscar documento, placa o estado…"
            />

            <div
              className="flex flex-wrap items-center gap-1 rounded-control bg-ink-50 p-1"
              role="group"
              aria-label="Filtro rápido de comprobantes"
            >
              {VISTAS_RAPIDAS.map((opcion) => {
                const activa = vista === opcion.valor;

                return (
                  <button
                    key={opcion.valor}
                    type="button"
                    onClick={() => setVista(opcion.valor)}
                    aria-pressed={activa}
                    className={cn(
                      'inline-flex h-8 items-center gap-1.5 rounded-control px-2.5 text-xs font-semibold transition-colors',
                      activa
                        ? 'bg-white text-brand-700 shadow-sm ring-1 ring-brand-200'
                        : 'text-ink-500 hover:bg-white/70 hover:text-ink-800',
                    )}
                  >
                    {opcion.etiqueta}
                    <span
                      className={cn(
                        'rounded-full px-1.5 py-px text-[10px] tabular-nums',
                        activa ? 'bg-brand-100 text-brand-700' : 'bg-ink-200/70 text-ink-600',
                      )}
                    >
                      {conteos[opcion.valor]}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {consulta.data && hayFiltroLocal ? (
            <div className="flex flex-wrap items-center gap-2 rounded-control border border-brand-100 bg-brand-50/60 px-3 py-2 text-xs text-ink-600">
              <Filter className="size-3.5 shrink-0 text-brand-600" aria-hidden="true" />
              <span>
                <strong className="font-semibold text-ink-800">{filtradas.length}</strong> de{' '}
                {facturas.length} comprobantes
                {vista !== 'todos' ? ` · ${etiquetaVista}` : ''}
                {busqueda.trim() ? (
                  <>
                    {' '}
                    que coinciden con{' '}
                    <strong className="font-semibold text-ink-800">“{busqueda.trim()}”</strong>
                  </>
                ) : null}
              </span>
              <button
                type="button"
                onClick={quitarFiltros}
                className="ml-auto inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold text-brand-700 transition-colors hover:bg-brand-100"
              >
                <X className="size-3" aria-hidden="true" />
                Quitar filtros
              </button>
            </div>
          ) : null}

          {consulta.isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : facturas.length === 0 ? (
            <EstadoVacio
              icon={Receipt}
              titulo="Sin comprobantes en ese rango"
              detalle="Prueba con otro rango de fechas, otra serie o sin filtros."
            />
          ) : filtradas.length === 0 ? (
            <EstadoVacio
              icon={Search}
              titulo="No hay comprobantes que coincidan"
              detalle={
                busqueda.trim()
                  ? `No encontramos nada para “${busqueda.trim()}”. Prueba con otro documento, placa o estado.`
                  : 'Ese filtro rápido no tiene comprobantes en el rango elegido.'
              }
              accion={
                <Button
                  variant="outline"
                  size="sm"
                  leftIcon={<RotateCcw className="size-3.5" aria-hidden="true" />}
                  onClick={quitarFiltros}
                >
                  Quitar filtros
                </Button>
              }
            />
          ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ink-100 text-left text-[11px] tracking-wide text-ink-400 uppercase">
                  <th className="py-2 font-semibold">Documento</th>
                  <th className="hidden py-2 font-semibold sm:table-cell">Emitido</th>
                  <th className="hidden py-2 font-semibold sm:table-cell">Vence</th>
                  <th className="py-2 text-right font-semibold">Total</th>
                  <th className="py-2 text-right font-semibold">Saldo</th>
                  <th className="hidden py-2 font-semibold sm:table-cell">Estado</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {paginacion.visibles.map((factura) => (
                  <tr
                    key={`${factura.origen}-${factura.id}`}
                    className="border-b border-ink-50 transition-colors hover:bg-brand-50/40"
                  >
                    <td className="py-2">
                      <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="font-mono text-xs whitespace-nowrap text-ink-800">
                          {factura.documento}
                        </span>
                        <Badge
                          variant={factura.origen === 'administrativa' ? 'brand' : 'outline'}
                          className="hidden sm:inline-flex"
                        >
                          {factura.origen === 'administrativa'
                            ? (factura.tipo ?? 'Administrativa')
                            : factura.origen === 'boleta'
                              ? 'Boleta'
                              : 'Factura'}
                        </Badge>
                      </span>
                      <span className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-ink-500 sm:hidden">
                        {formatDateValue(factura.fecha)}
                        <EstadoComprobante factura={factura} />
                      </span>
                    </td>
                    <td className="hidden py-2 text-ink-600 sm:table-cell">
                      {formatDateValue(factura.fecha)}
                    </td>
                    <td className="hidden py-2 text-ink-600 sm:table-cell">
                      {factura.vencimiento ? formatDateValue(factura.vencimiento) : '—'}
                    </td>
                    <td className="py-2 text-right font-medium tabular-nums text-ink-900">
                      {formatCurrency(factura.total)}
                    </td>
                    <td className="py-2 text-right tabular-nums text-ink-600">
                      {factura.saldo !== undefined ? formatCurrency(factura.saldo) : '—'}
                    </td>
                    <td className="hidden py-2 sm:table-cell">
                      <EstadoComprobante factura={factura} />
                    </td>
                    <td className="py-2 text-right">
                      <span className="flex justify-end gap-1">
                        {factura.origen === 'administrativa' ? (
                          <Button variant="ghost" size="sm" onClick={() => setDetalleId(factura.venta_id ?? factura.id)}>
                            Detalle
                          </Button>
                        ) : null}
                        {factura.pdf ? (
                          <a
                            href={factura.pdf}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="text-xs font-semibold text-brand-600 hover:underline"
                          >
                            PDF
                          </a>
                        ) : null}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>

        <Paginador {...paginacion} etiqueta="comprobantes" className="-mx-5 -mb-5 mt-3 rounded-b-card" />
      </SectionCard>

      {detalleId !== null ? (
        <DetalleFacturaPortal id={detalleId} onClose={() => setDetalleId(null)} />
      ) : null}
    </div>
  );
}

/** Detalle de una factura administrativa (items, retención y saldo). */
function DetalleFacturaPortal({ id, onClose }: { id: number; onClose: () => void }) {
  const consulta = usePortalFacturaDetalle(id);
  const factura = consulta.data?.factura;

  return (
    <Dialog open onClose={onClose} title={factura ? `Comprobante ${factura.documento}` : 'Comprobante'}>
      {consulta.isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : consulta.error || !factura ? (
        <Alert variant="error">
          {consulta.error instanceof ApiError
            ? consulta.error.message
            : 'No se pudo cargar el comprobante.'}
        </Alert>
      ) : (
        <div className="flex flex-col gap-4">
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <dt className="text-xs text-ink-400">Emitido</dt>
              <dd className="text-ink-800">{formatDateValue(factura.fecha)}</dd>
            </div>
            <div>
              <dt className="text-xs text-ink-400">Vence</dt>
              <dd className="text-ink-800">{formatDateValue(factura.vencimiento)}</dd>
            </div>
            <div>
              <dt className="text-xs text-ink-400">Condición</dt>
              <dd className="text-ink-800">{factura.condicion_pago ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-xs text-ink-400">Estado SUNAT</dt>
              <dd className="text-ink-800">{factura.estado}</dd>
            </div>
            <div>
              <dt className="text-xs text-ink-400">Total</dt>
              <dd className="font-semibold tabular-nums text-ink-900">
                {formatCurrency(factura.total)}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-ink-400">Saldo</dt>
              <dd className="font-semibold tabular-nums text-amber-600">
                {formatCurrency(factura.saldo)}
              </dd>
            </div>
          </dl>

          <div>
            <p className="mb-2 text-xs font-semibold tracking-wide text-ink-400 uppercase">
              Productos
            </p>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ink-100 text-left text-[11px] text-ink-400 uppercase">
                  <th className="py-1.5 font-semibold">Producto</th>
                  <th className="py-1.5 text-right font-semibold">Cantidad</th>
                  <th className="py-1.5 text-right font-semibold">Precio</th>
                  <th className="py-1.5 text-right font-semibold">Total</th>
                </tr>
              </thead>
              <tbody>
                {factura.items.map((item, indice) => (
                  <tr key={`${item.producto}-${indice}`} className="border-b border-ink-50">
                    <td className="py-1.5 text-ink-800">{item.producto}</td>
                    <td className="py-1.5 text-right tabular-nums text-ink-600">
                      {item.cantidad.toFixed(2)}
                    </td>
                    <td className="py-1.5 text-right tabular-nums text-ink-600">
                      {formatCurrency(item.precio)}
                    </td>
                    <td className="py-1.5 text-right font-medium tabular-nums text-ink-900">
                      {formatCurrency(item.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end">
            <Button variant="outline" size="sm" leftIcon={<X className="size-3.5" aria-hidden="true" />} onClick={onClose}>
              Cerrar
            </Button>
          </div>
        </div>
      )}
    </Dialog>
  );
}
